import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { RefreshCcw, Loader2, AlertTriangle } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

/**
 * RegradeAllTestsButton — admin tools for the athletic test database:
 *   1. Re-grade All Tests  → POST /api/athletic-tests/backfill-grades
 *   2. Clean Corrupt Values → POST /api/athletic-tests/cleanup-corrupt-values
 *
 * Both are safe to run multiple times.
 */
export default function RegradeAllTestsButton() {
  const [running, setRunning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);

  const run = async () => {
    if (running) return;
    if (!window.confirm(
      'Re-grade every athletic test record across all athletes? This applies the latest benchmark + alias + diagnostic rules. Safe to run multiple times — only changed records are written.'
    )) return;

    setRunning(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/athletic-tests/backfill-grades`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setLastResult(res.data);
      const { processed = 0, updated = 0 } = res.data;
      if (updated > 0) {
        toast.success(`Re-grading complete — ${updated} of ${processed} test records updated.`);
      } else {
        toast.success(`Re-grading complete — all ${processed} test records already up to date.`);
      }
    } catch (err) {
      console.error('Re-grade failed:', err);
      toast.error(err.response?.data?.detail || 'Re-grading failed. Check the server logs.');
    } finally {
      setRunning(false);
    }
  };

  const cleanup = async () => {
    if (cleaning) return;
    // Two-step: first dry-run
    const token = localStorage.getItem('token');
    setCleaning(true);
    try {
      const dry = await axios.post(
        `${BACKEND_URL}/api/athletic-tests/cleanup-corrupt-values`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      const totalBad = (dry.data.corrupt_max_speed_records || 0) + (dry.data.negative_power_records || 0);
      if (totalBad === 0) {
        setCleanupResult(dry.data);
        toast.success('Database is clean — no corrupt values found.');
        return;
      }
      const proceed = window.confirm(
        `Found ${dry.data.corrupt_max_speed_records} corrupt "Max Speed" records (values < 12 km/h — likely legacy import bugs) and ${dry.data.negative_power_records} negative-power records. Delete them all? This cannot be undone.`
      );
      if (!proceed) {
        setCleanupResult(dry.data);
        return;
      }
      const applied = await axios.post(
        `${BACKEND_URL}/api/athletic-tests/cleanup-corrupt-values?apply=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setCleanupResult(applied.data);
      const total = (applied.data.deleted_max_speed || 0) + (applied.data.deleted_power || 0);
      toast.success(`Deleted ${total} corrupt records. Re-run "Re-grade All Tests" to refresh grades.`);
    } catch (err) {
      console.error('Cleanup failed:', err);
      toast.error(err.response?.data?.detail || 'Cleanup failed. Check the server logs.');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="regrade-all-tests-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white flex items-center gap-2">
            <RefreshCcw className="h-4 w-4 text-emerald-400" />
            Re-grade All Athletic Tests
          </h3>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            Recalculates every athletic test record against the current benchmark library, test aliases, and diagnostic rules.
            Use this after a benchmark threshold is updated or when an athlete&apos;s existing grade looks wrong.
            Safe to run any time — only records whose grade or flags would change are written.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={cleanup}
            disabled={cleaning || running}
            variant="outline"
            className="h-11 px-4 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold uppercase text-xs tracking-wider"
            data-testid="cleanup-corrupt-values-btn"
            title="Removes physically-impossible values (e.g. distances mis-labelled as km/h, negative power readings)."
          >
            {cleaning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
            {cleaning ? 'Scanning…' : 'Clean Corrupt Values'}
          </Button>
          <Button
            onClick={run}
            disabled={running || cleaning}
            className="h-11 px-5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 font-bold uppercase text-xs tracking-wider"
            data-testid="regrade-all-tests-btn"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
            {running ? 'Re-grading…' : 'Re-grade All Tests'}
          </Button>
        </div>
      </div>

      {lastResult && (
        <div className="text-xs font-mono text-zinc-400 bg-zinc-950/60 border border-zinc-800 rounded p-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="regrade-result-summary">
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Processed</div><div className="text-white text-lg">{lastResult.processed}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Updated</div><div className="text-emerald-300 text-lg">{lastResult.updated}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Unchanged</div><div className="text-zinc-300 text-lg">{lastResult.unchanged}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Skipped</div><div className="text-amber-300 text-lg">{lastResult.skipped_missing_child}</div></div>
        </div>
      )}

      {cleanupResult && (
        <div className="text-xs font-mono text-zinc-400 bg-zinc-950/60 border border-amber-500/30 rounded p-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cleanup-result-summary">
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Mode</div><div className="text-white text-lg">{cleanupResult.mode}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Bad Max Speed</div><div className="text-amber-300 text-lg">{cleanupResult.corrupt_max_speed_records}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Bad Power</div><div className="text-amber-300 text-lg">{cleanupResult.negative_power_records}</div></div>
          <div><div className="text-zinc-600 uppercase text-[10px] tracking-wider">Deleted</div><div className="text-emerald-300 text-lg">{(cleanupResult.deleted_max_speed || 0) + (cleanupResult.deleted_power || 0)}</div></div>
        </div>
      )}
    </div>
  );
}
